import React, { useEffect, useState, useContext, useRef } from 'react';
import axios from "@common/axios";
import {
    Button,
    Form,
    Alert,
    Row,
    Col,
    Space,
    Spin,
    Modal,
} from 'antd';
import { UserContext } from "@context/UserContext";
import {BrandContext} from "@context/BrandContext";
import useCurrentShortcut from "@hooks/useCurrentShortcut";
import { useCloseTest, useUpdateTestResults, useSubmitTest } from './useStudentTest';
import useTestAutoSave from './useTestAutoSave';
import TestQuestion from "./TestQuestion";
import CircularLoader from "@common/CircularLoader";
import PageTabs from "./PageTabs";
import TestHeader from "./TestHeader";
import TestCountdown from "./TestCountdown";
import PreviewTest from './PreviewTest';
import SubmitTestConfirm from "./SubmitTestConfirm";
import GenericDialog from "@common/GenericDialog";
import {useScreenMode} from "@common/context/ScreenModeContext";
import ButtonsGroup from "@common/ButtonsGroup";

const Test = props => {
    const {trailTestAssignmentId, onContinue} = props;
    const { user } = useContext(UserContext);
    const {mobileScreenMode} = useScreenMode();
    const brandConfig = useContext(BrandContext);
    const [testAssignmentId, setTestAssignmentId] = useState();
    const [test, setTest] = useState();
    const [testResult, setTestResult] = useState();
    const [questionResults, setQuestionResults] = useState();
    const [testAssignmentResult, setTestAssignmentResult] = useState();
    const [testHighlighted, setTestHighlighted] = useState(false);
    const [isPreviewTestDialogOpen, setPreviewTestDialogOpen] = useState(false);
    const [isExpired, setExpired] = useState(false);
    const [isSubmitted, setSubmitted] = useState(false);
    const [totalAnswered, setTotalAnswered] = useState(0);
    const [showSubmitTestConfirm, setShowSubmitTestConfirm] = useState(false);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState();
    const [currentPage, setCurrentPage] = useState(1);
    const [form] = Form.useForm();
    const testRef = useRef();
    const { loadShortcutByUrl } = useCurrentShortcut();
    const { isUpdating, updateTestResults } = useUpdateTestResults({
        testRef,
        testResult,
        onError: setError,
    });
    const { isClosing, closeTest } = useCloseTest({
        testResult,
        testRef,
        loadShortcutByUrl,
        onError: setError,
    });
    const { isSubmitting, submitTest } = useSubmitTest({
        testResult,
        setTestAssignmentResult,
        setSubmitted,
        loadShortcutByUrl,
        onError: setError,
        displayToastMessage: displayToastMessage
    });

    const { startTestAutoSave, updateTabProgressBar} = useTestAutoSave(updateTestResults, () => {setExpired(true);}, setTotalAnswered);

    useEffect(() => {
        if(trailTestAssignmentId) {
            return;
        }

        const handleHashChange = (event) => {
            if (!window.location.hash) {
                return;
            }

            const hash = window.location.hash.slice(1);
            const hashNumericValue = Number(hash);
            if (!isNaN(hashNumericValue) && typeof hashNumericValue === 'number') {
                setTestAssignmentId(hashNumericValue);
            } else {
                setTestAssignmentId(undefined);
            }
        };
        handleHashChange();

        return () => {
            window.removeEventListener('hashchange', handleHashChange);
        };
    }, []);

    useEffect(() => {
        if(trailTestAssignmentId) {
            setTestAssignmentId(trailTestAssignmentId);
        }
    }, [trailTestAssignmentId]);

    const fetchData = (testAssignmentId) => {
        setLoading(true);
        setError(undefined);
        axios.get('/web/ui/test/startTest', {
            params: { testAssignmentId }
        })
            .then((response) => {
                setTest(response.data.test);
                setTestResult(response.data.testResult);
                setTotalAnswered(response.data.testResult?.totalAnswered || 0);
                setQuestionResults(response.data.questionResults);
            })
            .catch((error) => {
                setError('Unable to load test. Please try again. ' + error.message);
            })
            .finally(() => {
                setLoading(false);
            });
    };

    useEffect(() => {
        if (testAssignmentId === undefined) {
            return;
        }
        fetchData(testAssignmentId);
    }, [testAssignmentId]);

    const handleCloseTest = () => {
        if (testAssignmentId === undefined) {
            console.error('testAssignmentId is undefined.');
            return;
        }

        const handleOk = () => {
            if (testRef?.current) {
                $('.js-testContent', testRef.current).prop('disabled', true);
            }
            updateTestResults({onComplete: () => {
                Modal.destroyAll();
                closeTest();
            }});
        };

        if (testResult.testType === 'ASSESSMENT') {
            Modal.confirm({
                title: 'Exit test',
                content: (
                    <div>
                        <p>Closing this test will mean that your result will be recorded as not attempted.</p>
                        <p>Do you wish to continue?</p>
                    </div>
                ),
                width: 500,
                footer: () => (
                    <ButtonsGroup position={'right'}>
                        <Button className={'ant'} onClick={() => Modal.destroyAll()}>
                            No
                        </Button>
                        <Button className={'ant'} type="primary" onClick={handleOk}>
                            Yes
                        </Button>
                    </ButtonsGroup>
                )
            });
        } else {
            updateTestResults({onComplete: () => {
                if (testResult.testType === 'QUICK') {
                    loadShortcutByUrl('/controlCentre/showStudentTestsTabbed.action');
                } else {
                    loadShortcutByUrl('/web/ui/view/test/studentTestAssignments');
                }
            }});
        }
    };

    const handleExpireTest = () => {
        setExpired(true);
    };

    const handleSubmitTest = () => {
        setShowSubmitTestConfirm(true);
    }

    const handleSubmitConfirmClose = () => {
        if(trailTestAssignmentId && onContinue) {
            onContinue();
        } else {
            setShowSubmitTestConfirm(false);
            if(testAssignmentResult !== undefined) {
                loadShortcutByUrl('/web/ui/view/test/studentTestAssignments');
            }
        }
    }

    const handlePreviewTest = () => {
        if (testAssignmentId === undefined) {
            console.error('testAssignmentId is undefined.');
            return;
        }

        setPreviewTestDialogOpen(true);
    };

    const handleHighlightTest = () => {
        setTestHighlighted((highlighted) => !highlighted);
    };

    const handlePreviousPage = () => {
        setCurrentPage((page) => (page > 1 ? page - 1 : page));
    };

    const handleNextPage = () => {
        setCurrentPage((page) => (page < totalPages ? page + 1 : page));
    };

    const handleTestExpired = () => {
        loadShortcutByUrl('/web/ui/view/test/studentTestAssignments');
        setExpired(false);
        Modal.destroyAll();
    };

    useEffect(() => {
        if (!testRef.current) return;

        if (testHighlighted) {
            $('.js-question:not(.js-q-answered)', testRef.current).addClass('ch-highlight-bg');
        } else {
            $('.js-question:not(.js-q-answered)', testRef.current).removeClass('ch-highlight-bg');
        }
    }, [testHighlighted]);

    useEffect(() => {
        if (test == undefined || testRef.current == undefined) {
            return;
        }

        if(questionResults == undefined) {
            inlineQuestions.insertBatchAnswers($('.js-testContent', testRef.current), {}, false);
        } else {
            inlineQuestions.insertBatchAnswers($('.js-testContent', testRef.current), questionResults, false);
            updateTabProgressBar(testRef.current);
        }
        inlineQuestions.buildQuestionTypes($('.js-testContent', testRef.current));
        startTestAutoSave(testRef.current);

        const updateProgress = () => {
            updateTabProgressBar(testRef.current, setTotalAnswered);
        };

        $(document).on('q-updated', function() {
            updateTabProgressBar(testRef.current, setTotalAnswered);
        }).trigger('q-updated');

        return () => {
            document.removeEventListener('q-updated', updateProgress);
        };
    }, [test, testRef]);

    useEffect(() => {
        if(isExpired) {
            Modal.confirm({
                title: brandConfig.testName + ' Expired',
                content: <p className="ch-warning-text" style={{textAlign: 'left'}}>Your {brandConfig.testName.toLowerCase()} has expired.</p>,
                okCancel: false,
                footer: () => (
                    <ButtonsGroup position={'right'}>
                        <Button className={'ant'} type="primary" onClick={handleTestExpired}>
                            Return to {brandConfig.testsName}
                        </Button>
                    </ButtonsGroup>
                )
            });
        }
    }, [isExpired]);

    if (loading) {
        return <CircularLoader />;
    }

    if (test == undefined) {
        displayToastMessage('Your assessment may be complete, expired or invalid.');
        loadShortcutByUrl('/web/ui/view/test/studentTestAssignments');
    }

    if (testResult === undefined) {
        return <Space className="ch-error-blk" orientation="vertical" align="center">
            <Alert type="error" description="This assessment is not available."/>
            <Button type={'primary'} onClick={() => loadShortcutByUrl('/web/ui/view/test/studentTestAssignments')}>Return to your {brandConfig.testsName}</Button>
        </Space>;
    }

    if (!testResult.open) {
        return <Space className="ch-error-blk" orientation="vertical" align="center">
            <Alert type="error" description="This test has previously been attempted."/>
            <Button type={'primary'} onClick={() => loadShortcutByUrl('/web/ui/view/test/studentTestAssignments')}>Return to your {brandConfig.testsName}</Button>
        </Space>;
    }

    const questions = test.testQuestionBeans || [];
    const pageSize = 5;
    const totalPages = Math.ceil(questions.length / pageSize);
    const startIndex = (currentPage - 1) * pageSize;
    const endIndex = startIndex + pageSize - 1;

    const handlePageChange = (page) => {
        setCurrentPage(page);
    };

    const isProcessing = isUpdating || isClosing || isSubmitting;

    const getButtons = () => {
        if (mobileScreenMode) {
            return (
                <>
                    <Row justify="space-between" align="middle" className="margin-t--m">
                        <Button type="primary" onClick={handlePreviousPage} disabled={currentPage === 1}>
                            Previous
                        </Button>
                        <Button type="primary" onClick={handleNextPage} disabled={currentPage === totalPages}>
                            Next
                        </Button>
                    </Row>
                    <Row justify="center" className="margin-t--s">
                        <Space wrap>
                            <Button type="primary" onClick={handleHighlightTest}>
                                {testHighlighted ? 'Un-highlight' : 'Highlight'}
                            </Button>
                            {trailTestAssignmentId == null && (
                                <Button type="primary" onClick={handlePreviewTest}>
                                    Preview
                                </Button>
                            )}
                            <Button type="primary" onClick={handleSubmitTest}>
                                Submit
                            </Button>
                            {trailTestAssignmentId == null && (
                                <Button type="primary" onClick={handleCloseTest}>
                                    {test.testType === 'ASSESSMENT' ? 'Exit' : 'Close'}
                                </Button>
                            )}
                        </Space>
                    </Row>
                </>
            );
        } else {
            return (
                <Row justify="space-between" align="middle" className="margin-t--m">
                    <Button type="primary" onClick={handlePreviousPage} disabled={currentPage === 1}>
                        Previous
                    </Button>
                    <Space>
                        <Button type="primary" onClick={handleHighlightTest}>
                            {testHighlighted ? 'Un-highlight' : 'Highlight'}
                        </Button>
                        {trailTestAssignmentId == null && (
                            <Button type="primary" onClick={handlePreviewTest}>
                                Preview
                            </Button>
                        )}
                        <Button type="primary" onClick={handleSubmitTest}>
                            Submit
                        </Button>
                        {trailTestAssignmentId == null && (
                            <Button type="primary" onClick={handleCloseTest}>
                                {test.testType === 'ASSESSMENT' ? 'Exit' : 'Close'}
                            </Button>
                        )}
                    </Space>
                    <Button type="primary" onClick={handleNextPage} disabled={currentPage === totalPages}>
                        Next
                    </Button>
                </Row>
            );
        }
    };


    return <>
        <div className={'ch-test-wrapper'}>
            {error && <Alert type="error" description={error} />}
            <SubmitTestConfirm open={showSubmitTestConfirm} isLoading={isProcessing} onClose={handleSubmitConfirmClose} questionsRemaining={test.total - totalAnswered} testAssignmentResult={testAssignmentResult} submitTest={submitTest} updateTestResults={updateTestResults}/>
            <TestHeader test={test} testResult={testResult} />
            <Spin spinning={isSubmitting || isExpired}>
                <div
                    className="pw-card-padd js-test js-reactTest"
                    data-test-id={test.testId}
                    data-test-type={test.testType}
                    data-test-purpose={test.testPurpose}
                    data-test-status={testResult.testAssignmentStatus}
                    data-test-assignment-id={testResult.testAssignmentId}
                    data-step-id={test.stepId}
                    data-page={1}
                    data-highlighted={testHighlighted}
                    data-show-results={false}
                    data-test-submitted={isSubmitted}
                    data-test-expired={isExpired}
                    ref={testRef}
                >
                    {getButtons()}
                    <Row justify="space-between" align="middle">
                        <Col>
                            <span className="ch-text-bold">
                                {totalAnswered} of {questions.length} questions answered
                            </span>
                        </Col>
                        {testResult.endDate && (
                            <Col>
                                {isExpired ? (
                                    <span className="ch-error-text">Expired</span>
                                ) : (
                                    <TestCountdown endTime={testResult.endDate} onExpire={handleExpireTest} />
                                )}
                            </Col>
                        )}
                    </Row>
                    <PageTabs
                        totalPages={totalPages}
                        pageSize={pageSize}
                        test={test}
                        currentPage={currentPage}
                        onPageChange={handlePageChange}
                    />
                    <Form
                        form={form}
                        name={`testForm`}
                        layout="vertical"
                        className="js-testQuestionPage"
                        data-page={1}
                    >
                        <div className="js-testContent">
                            {questions.map((question, idx) => {
                                const questionNumber = idx + 1;

                                return (
                                    <div
                                        key={question.questionId}
                                        data-question={question.questionId}
                                        data-page={1}
                                        data-question-has-input={false}
                                        style={{ display: idx >= startIndex && idx <= endIndex ? 'block' : 'none' }}
                                    >
                                        <TestQuestion
                                            testQuestion={question}
                                            questionNumber={questionNumber}
                                            showExamCitations={!test.hideStudentExamCitations}
                                        />
                                    </div>
                                );
                            })}
                        </div>
                    </Form>
                    {getButtons()}
                </div>
            </Spin>
            <GenericDialog
                title="Preview Test"
                open={isPreviewTestDialogOpen}
                contentStyle={{ minWidth: 800 }}
                onClose={() => {setPreviewTestDialogOpen(false)}}
                buttons={[
                    { name: 'Close' },
                ]}
            >
                <PreviewTest testAssignmentId={testAssignmentId}/>
            </GenericDialog>
        </div>
    </>
};

export default Test;
